import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listSlots,
  createSlot,
  updateSlot,
  deleteSlot,
  AdminSlot,
  SlotStatus,
} from "../../../lib/adminApi";
import { useToast } from "../../../components/Toast";
import {
  ymd,
  startOfWeek,
  addDays,
  czShortDate,
  generateTimeSlots,
} from "../../../lib/dates";
import BatchCreateModal from "./BatchCreateModal";

/** Viditelné hodiny v gridu — 7:00–20:00 (lze později posunout). */
const VIEW_START = 7 * 60;
const VIEW_END = 20 * 60;
const TIME_ROWS = generateTimeSlots(VIEW_START, VIEW_END);

export default function Calendar() {
  const toast = useToast();
  const [weekStart, setWeekStart] = useState<Date>(startOfWeek(new Date()));
  const [slots, setSlots] = useState<AdminSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const reload = useCallback(() => {
    setLoading(true);
    listSlots(ymd(weekStart), ymd(addDays(weekStart, 6)))
      .then(setSlots)
      .catch((e) => toast.show("error", e.message))
      .finally(() => setLoading(false));
  }, [weekStart, toast]);

  useEffect(reload, [reload]);

  /** Lookup: key = "YYYY-MM-DD HH:MM" → slot */
  const byKey = useMemo(() => {
    const m = new Map<string, AdminSlot>();
    for (const s of slots) {
      const key = `${s.date.slice(0, 10)} ${s.startTime}`;
      m.set(key, s);
    }
    return m;
  }, [slots]);

  async function onCellClick(date: Date, time: string) {
    const key = `${ymd(date)} ${time}`;
    const existing = byKey.get(key);

    try {
      if (!existing) {
        // Prázdná buňka → vytvoř AVAILABLE
        await createSlot({ date: ymd(date), startTime: time, status: "AVAILABLE" });
      } else if (existing.status === "AVAILABLE") {
        // AVAILABLE → BLOCKED
        await updateSlot(existing.id, { status: "BLOCKED" });
      } else if (existing.status === "BLOCKED") {
        // BLOCKED → smazat (vrátí se do prázdna)
        await deleteSlot(existing.id);
      } else {
        // PENDING / RESERVED → jen info
        toast.show(
          "info",
          `Slot drží rezervaci "${existing.reservation?.snapshotName ?? ""}". Stav změň přes rezervaci.`
        );
        return;
      }
      reload();
    } catch (err: any) {
      toast.show("error", err.message ?? "Operace selhala.");
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kalendář</h1>
          <p className="mt-1 text-slate-600">
            Klik: prázdná → volná → blokováno → smazat. Sloty jsou 30-min.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            className="btn-secondary"
          >
            ← Předchozí
          </button>
          <button
            onClick={() => setWeekStart(startOfWeek(new Date()))}
            className="btn-secondary"
          >
            Dnešní týden
          </button>
          <button
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            className="btn-secondary"
          >
            Další →
          </button>
          <button onClick={() => setBatchOpen(true)} className="btn-primary">
            + Vygenerovat sloty
          </button>
        </div>
      </div>

      {/* Legenda */}
      <div className="mt-4 flex flex-wrap gap-3 text-xs">
        <Legend className="bg-emerald-500" label="Volné" />
        <Legend className="bg-amber-500" label="Čeká na schválení" />
        <Legend className="bg-obloha-indigo-600" label="Potvrzené" />
        <Legend className="bg-slate-400" label="Blokované" />
        <Legend className="bg-slate-100 border border-slate-300" label="Prázdné (zavřené)" />
      </div>

      {/* Grid */}
      <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <div className="grid min-w-[800px] grid-cols-[80px_repeat(7,1fr)]">
          {/* Hlavička */}
          <div className="border-b border-r border-slate-200 bg-slate-50"></div>
          {days.map((d, i) => (
            <div
              key={i}
              className="border-b border-l border-slate-200 bg-slate-50 px-2 py-2 text-center text-sm font-semibold text-slate-700"
            >
              {czShortDate(d)}
            </div>
          ))}

          {/* Řady: čas × den */}
          {TIME_ROWS.map((time) => (
            <TimeRow key={time} time={time} days={days} byKey={byKey} onCellClick={onCellClick} />
          ))}
        </div>
      </div>

      {loading && <div className="mt-3 text-center text-sm text-slate-500">Načítám…</div>}

      {batchOpen && (
        <BatchCreateModal
          initialWeekStart={weekStart}
          onClose={() => setBatchOpen(false)}
          onCreated={reload}
        />
      )}
    </div>
  );
}

function TimeRow({
  time,
  days,
  byKey,
  onCellClick,
}: {
  time: string;
  days: Date[];
  byKey: Map<string, AdminSlot>;
  onCellClick: (date: Date, time: string) => void;
}) {
  // Zobrazíme čas jen na celých hodinách
  const showLabel = time.endsWith(":00");

  return (
    <>
      <div className="flex items-start justify-end border-b border-r border-slate-200 pr-2 pt-1 text-xs font-medium text-slate-500">
        {showLabel ? time : ""}
      </div>
      {days.map((d, i) => {
        const key = `${ymd(d)} ${time}`;
        const slot = byKey.get(key);
        return (
          <Cell key={i} slot={slot} onClick={() => onCellClick(d, time)} />
        );
      })}
    </>
  );
}

function Cell({ slot, onClick }: { slot?: AdminSlot; onClick: () => void }) {
  const { bg, hover, label, textColor } = cellStyle(slot?.status);
  const title = slot
    ? slot.status === "PENDING" || slot.status === "RESERVED"
      ? `${slot.reservation?.snapshotName ?? "Rezervace"} · ${slot.reservation?.childrenCount ?? "?"} dětí`
      : slot.status === "BLOCKED"
      ? "Blokováno — klik smaže"
      : "Volný slot — klik zablokuje"
    : "Prázdné — klik otevře jako volný";

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-8 border-b border-l border-slate-200 text-[10px] font-medium transition ${bg} ${hover} ${textColor}`}
    >
      {label}
    </button>
  );
}

function cellStyle(status?: SlotStatus) {
  switch (status) {
    case "AVAILABLE":
      return { bg: "bg-emerald-500", hover: "hover:bg-emerald-600", label: "", textColor: "text-white" };
    case "PENDING":
      return { bg: "bg-amber-500", hover: "hover:bg-amber-600", label: "⏳", textColor: "text-white" };
    case "RESERVED":
      return { bg: "bg-obloha-indigo-600", hover: "hover:bg-obloha-indigo-700", label: "✓", textColor: "text-white" };
    case "BLOCKED":
      return { bg: "bg-slate-400", hover: "hover:bg-slate-500", label: "✕", textColor: "text-white" };
    default:
      return { bg: "bg-white", hover: "hover:bg-emerald-50", label: "", textColor: "text-slate-400" };
  }
}

function Legend({ className, label }: { className: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-slate-600">
      <span className={`inline-block h-3 w-4 rounded ${className}`} />
      {label}
    </div>
  );
}
